import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Paper,
    Chip,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { keyframes } from '@mui/system';

// --- Animações (Convertidas do CSS original) ---
const blink = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.1; transform: scale(0.9); }
`;

const breathe = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 15px var(--led-color); }
  50% { opacity: 0.4; transform: scale(0.95); box-shadow: 0 0 5px var(--led-color); }
`;

// --- Componente Visual do LED ---
const LedIndicator = ({ color, animation, speed = 'normal' }) => {
    let animationName = 'none';
    let duration = '1s';
    let timing = 'ease-in-out';

    if (animation === 'blink') {
        animationName = blink;
        timing = 'steps(1)'; // Piscar "seco/digital"
    } else if (animation === 'breathe') {
        animationName = breathe;
        duration = '2s';
    }

    if (animation === 'blink') {
        switch (speed) {
            case 'slow':
                duration = '2s';
                break;
            case 'normal':
                duration = '1s';
                break;
            case 'fast':
                duration = '0.5s';
                break;
            case 'super-fast':
                duration = '0.2s';
                break;
            default:
                duration = '1s';
        }
    }

    return (
        <Box
            sx={{
                '--led-color': color,
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: color,
                boxShadow: `0 0 12px ${color}, 0 0 20px ${color}`,
                border: '2px solid rgba(255,255,255,0.1)',
                animation: `${animationName} ${duration} ${timing} infinite`,
            }}
        />
    );
};

// --- Componente de Linha de Status ---
const StatusRow = ({
    color,
    animation,
    speed,
    title,
    meta,
    description,
    isError,
    isSectionHeader,
}) => {
    if (isSectionHeader) {
        return (
            <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(15, 23, 42, 0.5)', mt: 1 }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: '#64748b',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                    }}
                >
                    {title}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                borderBottom: '1px solid #334155',
                bgcolor: isError ? 'rgba(127, 29, 29, 0.1)' : 'transparent',
                borderLeft: isError ? '4px solid #ef4444' : 'none',
                '&:last-child': { borderBottom: 'none' },
                '&:hover': { bgcolor: '#27354f' },
                transition: 'background-color 0.2s',
            }}
        >
            <Box
                sx={{
                    width: 60,
                    display: 'flex',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <LedIndicator
                    color={color}
                    animation={animation}
                    speed={speed}
                />
            </Box>
            <Box sx={{ flexGrow: 1, pl: 2 }}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 'bold',
                        color: '#e2e8f0',
                        lineHeight: 1.2,
                    }}
                >
                    {title}
                </Typography>
                <Box
                    sx={{
                        mt: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                    }}
                >
                    <Chip
                        label={meta}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: '#334155',
                            color: '#94a3b8',
                            borderRadius: 1,
                            textTransform: 'uppercase',
                        }}
                    />
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        {description}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

// --- Card de Categoria ---
const CategoryCard = ({ title, colorHex, headerClass, children }) => (
    <Paper
        elevation={0}
        sx={{
            bgcolor: '#1e293b', // Slate 800
            border: '1px solid #334155',
            borderRadius: 3,
            overflow: 'hidden',
            mb: 3,
        }}
    >
        <Box
            sx={{
                p: 2,
                borderBottom: '1px solid #334155',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: '#0f172a',
            }}
        >
            <Box
                sx={{
                    width: 6,
                    height: 24,
                    borderRadius: 4,
                    bgcolor: colorHex,
                }}
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff' }}>
                {title}
            </Typography>
        </Box>
        <Box>{children}</Box>
    </Paper>
);

const LedStatusGuideDialog = ({ open, onClose }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{
                sx: {
                    bgcolor: '#0f172a', // Slate 900 background para todo o modal
                    color: '#e2e8f0',
                    backgroundImage: 'none',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #334155',
                    bgcolor: '#0f172a',
                }}
            >
                <Box>
                    <Typography
                        variant="h5"
                        sx={{ color: '#fff', fontWeight: 'bold' }}
                    >
                        Guia de LEDs do Sistema
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        Referência visual de cores e comportamentos das Torres
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: '#94a3b8' }}>
                    <Lucide.X />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: '#0f172a' }}>
                {/* === LED POWER === */}
                <CategoryCard title="LED POWER (Sistema)" colorHex="#22c55e">
                    {' '}
                    {/* Green 500 */}
                    <StatusRow
                        title="Sistema Ligado"
                        meta="Fixo"
                        description="Operação normal"
                        color="rgb(67, 160, 71)"
                        animation="none"
                    />
                    <StatusRow
                        title="Reset Nível 1 (Calibração)"
                        meta="Pisca (Médio)"
                        description="Pressionado por 1s"
                        color="rgb(253, 216, 53)"
                        animation="blink"
                        speed="normal"
                    />
                    <StatusRow
                        title="Reset Nível 2 (WiFi)"
                        meta="Pisca (Rápido)"
                        description="Pressionado por 10s (Cor Âmbar Escuro)"
                        color="rgb(245, 127, 23)"
                        animation="blink"
                        speed="fast"
                    />
                    <StatusRow
                        title="Reset Nível 3 (Fábrica)"
                        meta="Pisca (Muito Rápido)"
                        description="Pressionado por 30s - Apaga tudo"
                        color="rgb(229, 57, 53)"
                        animation="blink"
                        speed="super-fast"
                    />
                </CategoryCard>

                {/* === LED STATUS === */}
                <CategoryCard title="LED STATUS (Operação)" colorHex="#3b82f6">
                    {' '}
                    {/* Blue 500 */}
                    <StatusRow
                        title="Pré-aquecimento"
                        meta="Respira"
                        description="Sistema aquecendo componentes"
                        color="rgb(255, 179, 0)"
                        animation="breathe"
                    />
                    <StatusRow
                        title="Pronto / Aguardando"
                        meta="Respira"
                        description="Temperatura estável, aguardando tubo"
                        color="rgb(67, 160, 71)"
                        animation="breathe"
                    />
                    <StatusRow
                        title="Tubo Inserido"
                        meta="Pisca"
                        description="Tubo detectado, sem experimento ativo"
                        color="rgb(30, 136, 229)"
                        animation="blink"
                        speed="normal"
                    />
                    <StatusRow
                        title="Calibrando Sensores"
                        meta="Respira (Ciano)"
                        description="Início do experimento"
                        color="rgb(0, 172, 193)"
                        animation="breathe"
                    />
                    <StatusRow
                        title="Experimento (Cloud)"
                        meta="Respira (Azul)"
                        description="Rodando protocolo conectado"
                        color="rgb(30, 136, 229)"
                        animation="breathe"
                    />
                    <StatusRow
                        title="Experimento (Local)"
                        meta="Respira (Roxo)"
                        description="Sem protocolo ou modo offline"
                        color="rgb(142, 36, 170)"
                        animation="breathe"
                    />
                    <StatusRow
                        title="Modo Telemetria"
                        meta="Fixo (Roxo)"
                        description="Aguardando comandos"
                        color="rgb(142, 36, 170)"
                        animation="none"
                    />
                    {/* Divider Baseline */}
                    <StatusRow
                        isSectionHeader
                        title="Calibração de Fábrica (Baseline)"
                    />
                    <StatusRow
                        title="Aguardando Tubo (Baseline)"
                        meta="Pisca (Roxo Escuro)"
                        description="Inserir padrão de calibração"
                        color="rgb(94, 53, 177)"
                        animation="blink"
                        speed="normal"
                    />
                    <StatusRow
                        title="Calibrando Baseline"
                        meta="Respira (Lento)"
                        description="Processo crítico, não desligar"
                        color="rgb(94, 53, 177)"
                        animation="breathe"
                        speed="slow"
                    />
                    <StatusRow
                        title="Remover Tubo"
                        meta="Pisca (Branco)"
                        description="Baseline completo"
                        color="#ffffff"
                        animation="blink"
                        speed="normal"
                    />
                    <StatusRow
                        title="Falha / Erro"
                        meta="Pisca (Vermelho)"
                        description="Falha na calibração ou sensor"
                        color="rgb(229, 57, 53)"
                        animation="blink"
                        speed="fast"
                        isError
                    />
                </CategoryCard>

                {/* === LED CLOUD === */}
                <CategoryCard
                    title="LED CLOUD (Conectividade)"
                    colorHex="#f59e0b"
                >
                    {' '}
                    {/* Amber 500 */}
                    <StatusRow
                        title="Conectando WiFi"
                        meta="Pisca (Âmbar)"
                        description="Tentando conexão com roteador"
                        color="rgb(255, 179, 0)"
                        animation="blink"
                        speed="normal"
                    />
                    <StatusRow
                        title="WiFi Conectado (Sem Internet)"
                        meta="Fixo (Âmbar)"
                        description="IP obtido, mas cloud offline"
                        color="rgb(255, 179, 0)"
                        animation="none"
                    />
                    <StatusRow
                        title="Online / Conectado"
                        meta="Fixo (Verde)"
                        description="WiFi OK + MQTT OK"
                        color="rgb(67, 160, 71)"
                        animation="none"
                    />
                    <StatusRow
                        title="Modo Configuração (App)"
                        meta="Pisca (Branco)"
                        description="SmartConfig / BLE ativo"
                        color="#ffffff"
                        animation="blink"
                        speed="normal"
                    />
                    {/* Divider OTA */}
                    <StatusRow
                        isSectionHeader
                        title="Atualização de Firmware (OTA)"
                    />
                    <StatusRow
                        title="Verificando Atualização"
                        meta="Respira (Azul Claro)"
                        description="Checkagem de versão"
                        color="rgb(3, 155, 229)"
                        animation="breathe"
                    />
                    <StatusRow
                        title="Baixando Atualização"
                        meta="Pisca (Rosa)"
                        description="Download em progresso (Não desligar)"
                        color="rgb(216, 27, 96)"
                        animation="blink"
                        speed="super-fast"
                    />
                    <StatusRow
                        title="Erro na Atualização"
                        meta="Fixo (Vermelho)"
                        description="Falha no download ou certificado"
                        color="rgb(229, 57, 53)"
                        animation="none"
                        isError
                    />
                </CategoryCard>

                <Box sx={{ mt: 4, textAlign: 'center', color: '#64748b' }}>
                    <Typography variant="caption">
                        Infográfico gerado com base em ui_led_configs.h
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    borderTop: '1px solid #334155',
                    bgcolor: '#0f172a',
                    p: 2,
                }}
            >
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        bgcolor: '#334155',
                        '&:hover': { bgcolor: '#475569' },
                    }}
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LedStatusGuideDialog;
